<template>
    <div>
        <popup-modal
            icon="forum"
            :close-on-click="true"
            :title="title"
            :confirm="true"
            :dirty="form?.isDirty()"
            @close="$emit('close')"
        >
            <div class="w-768">
                <banner
                    v-if="form.rescheduling"
                    :description="$t(`interview.reschedule_notice`)"
                    :class="[isCanceled || bulkApplications.length > 1 ? 'mb-12' : 'mb-20']"
                    inline
                    info
                />

                <banner
                    v-if="isCanceled"
                    :description="$t('interview.cancelled')"
                    class="mb-20"
                    inline
                    danger
                />

                <banner
                    v-if="!isCanceled && bulkApplications.length > 1"
                    :description="$t('interview.alerts.bulk_warning', { application_list: applicationList})"
                    class="-mt-2 mb-20"
                    inline
                    warning
                />

                <banner
                    v-if="isOnlineMeeting && !form.meeting_url && form.starts_at && form.type !== 'phone'"
                    :description="$t('interview.no_url_warning', {date: interviewDate, type: form.type})"
                    inline
                    danger
                />

                <div v-else-if="isOnlineMeeting && form.meeting_url && !form.starts_at && form.type !== 'online'"
                     class="-mt-16 mb-32 pt-24 pl-24 bg-primary rounded-6 overflow-hidden">
                    <div class="mr-24 mb-24 flex items-center">
                        <div class="mr-24">
                            <a :href="form.meeting_url"
                               class="rounded-6 px-16 h-40 flex items-center justify-center"
                               target="_blank">
                                <interview-icons :type="form.type"/>
                            </a>
                        </div>
                        <div class="text-body-sm text-white">
                            {{ $t(`interview.${form.type}_start`) }}
                        </div>
                    </div>
                </div>

                <div v-if="interview.internal_notes" class="rounded-6 p-12 bg-primary-40 border border-primary mb-32"
                     v-html="interview.internal_notes"/>

                <div class="flex flex-wrap">
                    <div class="w-1/2 pr-16">
                        <label class="mb-8 text-body-sm flex items-center">
                            <span class="first-letter:uppercase font-semibold text-black">
                                {{ $t('interview.name') }}
                            </span>
                        </label>
                        <simple-input
                            v-model="form.name"
                            :disabled="!actionAllowed"
                            class="mb-16 first-letter:uppercase">
                        </simple-input>
                    </div>

                    <div class="w-1/2 pl-8 flex justify-between">
                        <div v-for="type in subTypes">
                            <label class="mb-8 text-body-sm flex items-center">
                                <span class="first-letter:uppercase font-semibold text-black">
                                    {{ $t(`subscriptions.subscribed_${type}_title`) }}
                                </span>
                            </label>
                            <div class="flex items-center">
                                <div :class="subscriptionList(type).length ? 'mr-16' : ''" class="flex">
                                    <template v-for="sub in subscriptionList(type)" :key="sub.user.id">
                                        <tooltip
                                            v-if="sub.user"
                                            :content="sub.user.name"
                                            class="-mr-8"
                                            position="bottom"
                                        >
                                            <avatar
                                                :image="sub.user.image_url"
                                                size="small"
                                                class="cursor-default border border-white"
                                            >{{ sub.user.initials }}
                                            </avatar>
                                        </tooltip>
                                    </template>
                                </div>
                                <button v-if="actionAllowed"
                                        class="h-32 w-32 p-2 flex font-semibold items-center justify-center rounded-32 bg-primary hover:bg-primary-80 text-white trans-ease"
                                        @click="manageSubscriptions">
                                    <svg-icon size="16" type="add"></svg-icon>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="w-1/2 pr-16">
                        <div class="mb-16">
                            <label class="mb-8 text-body-sm flex items-center">
                                <span class="first-letter:uppercase font-semibold text-black">
                                    {{ $t('interview.timeslots.suggested_timeslots') }}
                                </span>
                            </label>
                            <div class="mb-16">
                                <loading v-if="fetchingInterview" size="small" align="left"></loading>
                                <div v-else>
                                    <div v-for="(timeslot, index) in currentTimeslots.slice(0, 5)"
                                         :key="index"
                                    >
                                        {{ startDate(timeslot.date) }}
                                        {{ form.duration ? '- ' + endDate(timeslot.date) : '' }}
                                    </div>
                                    <div v-if="form.time_slots.length > 5">
                                   <span class="italic">
                                        {{
                                           `(+${form.time_slots.length - 5} ${$t('general.other').toLowerCase()})`
                                       }}
                                    </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="flex">
                            <simple-button v-if="canManageTimeSlots" class="mb-16"
                                           @click="startEditingTimeslots">
                                {{ $t('interview.edit_time_slots') }}
                            </simple-button>
                        </div>
                    </div>

                    <div class="w-1/2 pl-8 mt-8">
                        <label class="mb-8 text-body-sm flex items-center">
                            <span class="first-letter:uppercase font-semibold text-black">
                                {{ $t('interview.location_or_url') }}
                            </span>
                        </label>
                        <div class="flex items-center space-x-8">
                            <template v-if="!form.type">
                                {{ $t('interview.no_location_available') }}
                            </template>
                            <template v-else>
                                <span v-if="form.type === 'location'">
                                    {{ location }}
                                </span>
                                <span v-else-if="form.type === 'phone'">
                                    {{ $t('interview.through_phone') }}
                                </span>
                                <div v-else class="flex items-center">
                                    <interview-icons :type="form.type" class="max-h-24"/>

                                    <simple-input
                                        :class="form.type === 'zoom' ? 'max-w-240' : ''"
                                        colored-after
                                        readonly
                                        :value="form.meeting_url"
                                    >
                                        <template #after>
                                            <template v-if="isValidLink">
                                                <a
                                                    class="cursor-pointer"
                                                    :href="interviewUrl"
                                                    target="_blank"
                                                >
                                                    <svg-icon type="keyboard-arrow-right"/>
                                                </a>
                                            </template>
                                            <template v-else>
                                                <div class="cursor-not-allowed">
                                                    <svg-icon type="keyboard-arrow-right"/>
                                                </div>
                                            </template>
                                        </template>
                                    </simple-input>
                                </div>
                            </template>

                            <span v-if="actionAllowed && (!interviewIsConfirmed || form.rescheduling || instant)"
                                  class="w-32 h-32 ml-12 cursor-pointer bg-primary-5 flex items-center justify-center rounded-32 group hover:bg-primary-5"
                                  @click="toggleEditingType">
                                <svg-icon
                                    class="text-primary-80 group-hover:text-primary-80 transition duration-150 cursor-pointer"
                                    size="16"
                                    type="edit"
                                />
                            </span>
                        </div>
                    </div>

                    <div v-if="form.invitation && !form.starts_at"
                         class="w-full mt-16 rounded-6 bg-semantic-warning-light p-16">
                        <p class="text-semantic-warning">
                            <span class="font-semibold">{{ $t('interview.warning_time_edit_careful') }}</span>
                            {{ $t('interview.warning_time_edit_message') }}
                        </p>
                    </div>
                </div>

                <div class="w-1/2 mt-8 pr-16 space-y-8">
                    <div class="font-semibold text-black">{{ $t('interview.date') }}</div>
                    <div v-if="form.starts_at">
                        {{ formatDateTimeWithDuration(interviewDate) }}
                    </div>
                    <div v-else-if="interview.instant">
                        {{ formatDateTimeWithDuration(formatFirstTimeslot) }}
                    </div>
                    <div v-else-if="interview.status" class="italic">
                        {{ interview.status.name }}
                    </div>
                    <p v-else-if="invitationForm" class="italic">
                        {{ $t('interview.status.waiting_for_confirmation') }}
                    </p>
                    <div>
                        <simple-button
                            display="primary"
                            download
                            look="primary"
                            :href="interview.icsFilepath"
                            type="link"
                            v-if="interview.icsFilepath"
                        >
                            {{ $t('interview.download_ics') }}
                        </simple-button>
                    </div>
                </div>

                <loading v-if="fetchingInterview"></loading>
                <div v-else class="relative">
                    <template v-if="questions && questions.length > 0">
                        <div v-if="printing" class="z-top absolute w-full h-full backdrop-blur flex items-center justify-center">
                            <loading/>
                        </div>
                        <div id="interview-questions"
                             :class="!printing ? 'border-t border-primary-5 mt-16 pt-16' : 'p-16'"
                        >
                            <p v-if="printing" class="block text-heading-2 mb-32 font-semibold">
                                {{ $t('interview.interview') + ' ' + $t('general.with') + ' ' + application.fullName }}
                            </p>

                            <div v-for="(answer, index) in answers"
                                 :key="'answer-' + answer.id"
                                 class="flex mb-32"
                            >
                                <p class="text-secondary font-semibold text-heading-2 mr-16">Q{{ index + 1 }}</p>
                                <interview-answer
                                    :ref="'answer-' + answer.id"
                                    :answer="answer"
                                    :disabled="!actionAllowed"
                                    :show-delete-button="!printing"
                                    :tab-index="index + 1"
                                    :toggle-fields-to-text="printing"
                                    class="flex-grow"
                                />
                            </div>
                        </div>
                        <div
                            class="justify-between items-center"
                            :class="printing ? 'hidden' : 'flex'"
                        >
                            <simple-button
                                :disabled="performingAddAnswerRequest || !actionAllowed"
                                icon="add-circle"
                                :loading="performingAddAnswerRequest"
                                @click="addAnswer"
                            >
                                {{ $t('settings.interview-templates.new_question') }}
                            </simple-button>

                            <!-- Print button -->
                            <print-content
                                :title="$t('interview.interview') + ' ' + $t('general.with') + ' ' + application.fullName"
                                hide-button
                                content="interview-questions"
                                @printing="printing = $event"
                            />
                        </div>
                    </template>
                </div>


                <div v-if="actionAllowed && questions.length === 0" class="my-16">
                    <div class="text-center my-16">
                        <p class="text-heading-2 ">{{ $t('interview.start.title') }}</p>
                        <strong>{{ $t('interview.start.sub_title') }}</strong>
                    </div>

                    <div class="grid grid-cols-1 gap-32 sm:grid-cols-3">
                        <button
                            :disabled="!defaultInterviewTemplate || fetchingDefaultQuestions"
                            class="flex flex-col justify-center items-center gap-16 focus:outline-none relative border border-primary-20 shadow rounded-6 p-12"
                            :class="{'opacity-50 cursor-not-allowed': !defaultInterviewTemplate || fetchingDefaultQuestions, 'cursor-pointer hover:bg-primary-5': defaultInterviewTemplate && !fetchingDefaultQuestions}"
                            role="button"
                            @click="importDefaultTemplateQuestions"
                        >
                            <loading v-if="fetchingDefaultQuestions"/>
                            <svg-icon size="64" type="list-alt-filled" v-else/>
                            <span>
                                <span class="block text-body mb-4 first-letter:uppercase font-semibold leading-tight">
                                    {{ $t('interview.start.choice3') }}
                                </span>
                                <span class="block">
                                    <template v-if="defaultInterviewTemplate && defaultInterviewTemplate.title && !fetchingDefaultTemplate">
                                        {{ $t('interview.start.choice3_description') }}
                                        {{ defaultInterviewTemplate.titleLocale }}
                                    </template>
                                    <template v-else>
                                        {{ $t('interviews.no_default_interview_template') }}
                                    </template>
                                </span>
                            </span>
                        </button>
                        <button
                            class="flex flex-col justify-center items-center gap-16 cursor-pointer focus:outline-none relative border border-primary-20 hover:bg-primary-5 shadow rounded-6 p-12"
                            role="button"
                            @click="importQuestions"
                        >
                            <loading v-if="performingImportQuestions"/>
                            <svg-icon size="64" type="forum-filled" v-else/>
                            <span>
                                <span class="block text-body mb-4 first-letter:uppercase font-semibold leading-tight">
                                    {{ $t('interview.start.choice1') }}
                                </span>
                                <span class="block">{{ $t('interview.start.choice1_description') }}</span>
                            </span>
                        </button>
                        <button
                            :disabled="performingAddAnswerRequest"
                            class="flex flex-col justify-center items-center gap-16 cursor-pointer focus:outline-none relative border border-primary-20 hover:bg-primary-5 shadow rounded-6 p-12"
                            role="button"
                            @click="addAnswer"
                        >
                            <loading v-if="performingAddAnswerRequest"/>
                            <svg-icon size="64" type="question-mark" v-else/>
                            <span>
                                <span class="block text-body mb-4 first-letter:uppercase font-semibold leading-tight">
                                    {{ $t('interview.start.choice2_title') }}
                                </span>
                                <span class="block">{{ $t('interview.start.choice2') }}</span>
                            </span>
                        </button>
                    </div>
                </div>

                <div class="mt-32">
                    <simple-textarea v-model="form.feedback" class="mb-16">{{
                            $t('applications.feedback_label')
                        }}
                    </simple-textarea>
                </div>

                <div v-if="rescheduledByInterviewee || cancelledByInterviewee"
                     class="border border-primary-5 bg-primary-5 rounded-4 px-24 mb-16"
                >
                    <button class="trans-ease flex items-center text-primary-80 hover:text-primary text-body w-full py-16"
                            role="button"
                            @click="showReason = !showReason"
                    >
                        <span v-if="rescheduledByInterviewee">{{ $t('interview.show_reschedule_reason') }}</span>
                        <span v-else-if="cancelledByInterviewee">{{ $t('interview.show_cancel_reason') }}</span>
                        <svg-icon
                            class="ml-12 text-secondary-80"
                            type="keyboard-arrow-down"
                            size="24"
                            v-if="showReason"
                        />
                        <svg-icon
                            class="ml-12 text-secondary-80"
                            size="24"
                            type="keyboard-arrow-right"
                            v-else
                        />
                    </button>

                    <div v-if="showReason" class="pb-24">
                        <div class="bg-white rounded-4 p-16">
                            <p class="text-body" v-html="reason"></p>
                        </div>
                    </div>
                </div>

                <div class="flex items-center justify-end gap-20 pt-32">
                    <simple-button
                        v-if="actionAllowed && !this.interview.instant"
                        display="danger"
                        :loading="cancelingInterview"
                        look="primary"
                        @click="cancelingInterview = true"
                    >
                        {{ $t('interview.buttons.cancel') }}
                    </simple-button>
                    <simple-button
                        v-if="actionAllowed && !instant && interviewIsConfirmed"
                        :disabled="form.rescheduling"
                        look="primary"
                        :capitalize="true"
                        :uppercase="false"
                        @click="rescheduleHandler"
                    >
                        {{ $t('interview.buttons.reschedule') }}
                    </simple-button>
                    <simple-button
                        v-if="actionAllowed || isCanceled"
                        :loading="form.submitting"
                        display="secondary"
                        look="primary"
                        type="button"
                        :capitalize="true"
                        :uppercase="false"
                        @click="submitHandler"
                    >
                        {{ submitButtonLabel }}
                    </simple-button>
                </div>
            </div>
        </popup-modal>

        <question-importer
            v-if="importingQuestions"
            :current-questions="questions"
            :interview="interview"
            @close="stopImportingQuestions"
        />

        <popup-modal-confirm
            v-if="showConfirmation"
            :cancel-text="isBulk ? $t('interview.extra_time_slot') : $t('general.cancel')"
            :swap-button-logic="isBulk"
            confirm-button="success"
            :confirmText="$t('general.confirm')"
            :message="confirmMessage"
            :title="formTitle"
            :working="form.submitting"
            @close="cancelSendingMail"
            @confirm="saveInterview"
        >
            <checkbox
                v-if="formHasNotifiableProps"
                v-model="form.send_mail"
                class="mr-8 my-16 whitespace-nowrap"
            >
                <span class="font-normal">{{ $t('interview.send_update_mail') }}</span>
            </checkbox>
            <div v-if="form.send_mail && !interview.instant && formHasNotifiableProps">
                <div class="w-672 mt-24">
                    <label for="bcc-search-input" class="block mb-8 flex items-center">
                        {{ $t('mailer.add_bcc') }}
                        <span class="text-body-sm">({{ $t('general.optional') }})</span>
                    </label>
                    <bcc-fields v-model="updateForm.bcc" class="mb-16"></bcc-fields>

                    <select-dropdown
                        v-model="selectedTemplate"
                        :auto-translate="false"
                        :options="templateChoices"
                        no-auto-sort
                        class="mb-16"
                        @change="hydrateFromTemplate"
                    >
                        {{ $t('mailer.choose_template') }}

                        <tabs v-if="languages"
                              :active-key="templateLocale"
                              :items="languages"
                              class="inline-flex"
                              @set-active="hydrateFromTemplate"
                        />
                        <span v-if="application.locale !== this.templateLocale">{{ $t('interview.error_candidate_template_language') }}</span>
                    </select-dropdown>

                    <simple-input v-show="!form.slot_confirmed"
                                  v-model="updateForm.subject"
                                  class="mb-16"
                    >
                        {{ $t('mailer.subject') }}
                    </simple-input>

                    <wysiwyg v-show="!form.slot_confirmed"
                             ref="content"
                             v-model="updateForm.message"
                             :placeholders="['application', 'company', 'user', 'interviewInvitation']"
                             class="mb-16"
                             with-links
                    >
                        {{ $t('mailer.description') }}

                        <template #after>
                            <div
                                :class="[ signaturePreview ? 'items-start' : 'items-center text-primary-40' ]"
                                class="flex p-16 border-t border-primary-20"
                            >
                                <div class="text-body flex-grow">
                                    <span v-if="signaturePreview" v-html="signaturePreview"></span>
                                    <span v-else>{{ $t('email_signatures.no_signature_selected') }}</span>
                                </div>
                                <select-dropdown
                                    v-model="selectedSignature"
                                    :auto-translate="false"
                                    :options="signatureChoices"
                                    class="min-w-240"
                                />
                            </div>
                        </template>
                    </wysiwyg>
                </div>

                <div class="border border-primary-5 bg-primary-5 rounded-4 px-24 my-32 w-672">

                    <button
                        class="trans-ease flex items-center text-primary-80 hover:text-primary text-body w-full py-16"
                        role="button"
                        @click="showUpdateForm = !showUpdateForm"
                    >
                        {{ confirmTitle }}
                        <svg-icon
                            class="ml-12 text-secondary-80"
                            size="24"
                            type="keyboard-arrow-up"
                            v-if="showUpdateForm"
                        />
                        <svg-icon
                            class="ml-12 text-secondary-80"
                            size="24"
                            type="keyboard-arrow-down"
                            v-else
                        />
                    </button>

                    <div v-show="showUpdateForm">
                        <simple-input v-model="formSubject"
                                      class="my-16"
                        >
                            {{ $t('settings.email-templates.subject') }}
                        </simple-input>

                        <wysiwyg ref="confirmation_content"
                                 v-model="confirmationContent"
                                 :placeholders="['application', 'company', 'user', 'interviewInvitation', 'interviewInvitationConfirmed']"
                                 class="mb-16"
                                 with-links
                        >
                            {{ $t('form.content') }}

                            <template #after>
                                <div
                                    :class="[ signaturePreview ? 'items-start' : 'items-center text-primary-40' ]"
                                    class="flex p-16 border-t border-primary-20"
                                >
                                    <div class="text-body flex-grow">
                                        <span v-if="signaturePreview" v-html="signaturePreview"></span>
                                        <span v-else>{{ $t('email_signatures.no_signature_selected') }}</span>
                                    </div>
                                    <select-dropdown
                                        v-model="selectedSignature"
                                        :auto-translate="false"
                                        :options="signatureChoices"
                                        class="min-w-240"
                                    ></select-dropdown>
                                </div>
                            </template>

                        </wysiwyg>
                    </div>
                </div>
            </div>
        </popup-modal-confirm>

        <subscriber-manager
            v-if="managingSubscriptions"
            :resource-title="$t('interview.interview')"
            :subs="subscriptions"
            :target="interview"
            :translations="subscriberTranslations"
            @close="stopManagingSubscribers"
        />

        <interview-cancel
            v-if="cancelingInterview"
            v-model="cancellationMail"
            :send-mail="form.send_mail"
            :loading="savingInterview"
            @changeSendMail="form.send_mail = $event"
            @close="cancelingInterview = false"
            @confirm="cancelInterview($event)"
        />

        <popup-modal v-if="editingType" customClasses="w-768" @close="toggleEditingType">
            <interview-type-fields
                v-model="form"
                :can-manage="actionAllowed"
                @saved="toggleEditingType"
            />
        </popup-modal>

        <popup-modal
            v-if="editingTimeslots && (hasAccessToCalendar)"
            customClasses="w-768"
            position="center"
            @close="stopEditingTimeslots('close')">
            <interview-mode-manager
                :form="form"
                :subs="requiredSubscriptions"
                :alerts="timeslotsAlerts"
                :has-bulk-timeslots="!!interview.has_bulk_timeslots"
                :bulk-applications="bulkApplications"
                :disabled="(interviewIsConfirmed && !instant) && !form.rescheduling"
                :can-manage="actionAllowed"
                @saved="stopEditingTimeslots"
            ></interview-mode-manager>
        </popup-modal>

        <popup-modal
            v-if="editingTimeslots && (!hasAccessToCalendar)"
            customClasses="w-768"
            position="center"
            @close="stopEditingTimeslots('close')"
        >
            <interview-timeslots
                v-model="form"
                :update="true"
                :alerts="timeslotsAlerts"
                :has-bulk-timeslots="!!interview.has_bulk_timeslots"
                :bulk-applications="bulkApplications"
                :disabled="(interviewIsConfirmed && !instant) && !form.rescheduling"
                :can-manage="actionAllowed"
                :subs="requiredSubscriptions"
                @saved="stopEditingTimeslots"
            />
        </popup-modal>
    </div>
</template>

<script>
import * as interviewTemplateService from "../services/InterviewTemplateService";
import * as interviews from '../services/InterviewService';
import PopupModal from './ui/PopupModal.vue';
import Application from '../models/Application';
import SimpleButton from './ui/SimpleButton.vue';
import Interview from '../models/Interview';
import InterviewAnswer from '../models/InterviewAnswer';
import {normalizeJson} from '../../shared/helpers';
import Loading from './ui/Loading.vue';
import Form from '../etc/forms/Form';
import TextArea from './forms/TextArea.vue';
import QuestionImporter from './ui/QuestionImporter.vue';
import {default as InterviewAnswerComponent} from './ui/InterviewAnswer.vue';
import SubscriberList from './ui/SubscriberList.vue';
import InterviewIcons from './InterviewIcons.vue';
import SvgIcon from './ui/SvgIcon.vue';
import Tooltip from './navigation/Tooltip.vue';
import Avatar from './ui/Avatar.vue';
import SubscriberManager from './ui/SubscriberManager';
import Subscription from '../models/Subscription';
import InterviewTypeFields from './InterviewTypeFields';
import moment from 'moment';
import DatePicker from './forms/DatePicker.vue';
import InterviewTimeslots from './InterviewTimeslots.vue';
import Input from './forms/Input.vue';
import PrintContent from './jobs/jobstats/PrintContent.vue';
import InterviewTemplate from "../models/InterviewTemplate";
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import {useAuthenticationStore} from '../state/modules/authentication';
import {useCompaniesStore} from '../state/modules/companies';
import {useInterviewStore} from '../state/modules/interviews';
import * as subscriptionService from '../services/SubscriptionService';
import EmailSignature from "../models/EmailSignature";
import SelectDropdown from "./forms/SelectDropdown";
import PopupModalConfirm from "./ui/PopupModalConfirm";
import Wysiwyg from "./forms/Wysiwyg";
import InterviewCancel from "./InterviewCancel";
import Location from "../models/Location";
import * as signatures from "../services/EmailSignatureService";
import * as templates from "../services/EmailTemplateService";
import EmailTemplate from "../models/EmailTemplate";
import Timeslot from "../models/Timeslot";
import Tabs from "./navigation/Tabs.vue";
import FileUpload from "./forms/FileUpload.vue";
import BccFields from "./ui/BccFields.vue";
import Checkbox from "./forms/Checkbox.vue";
import CalendarSyncEdit from './ui/CalendarSyncEdit.vue';
import Feature from "./Feature.vue";
import AvailabilityScheduler from "./calendar/AvailabilityScheduler.vue";
import InterviewModeManager from "./interviews/InterviewModeManager.vue";
import User from "../models/User";
import TimelineEvent from "../models/TimelineEvent";
import Banner from "./ui/Banner.vue";
import {isNumber} from "lodash";

export default {
    components: {
        Banner,
        InterviewModeManager,
        AvailabilityScheduler,
        Feature,
		Checkbox,
        BccFields,
        FileUpload,
        Tabs,
        CalendarSyncEdit,
        PopupModalConfirm,
        InterviewTimeslots,
        DatePicker,
        InterviewTypeFields,
        SubscriberManager,
        Avatar,
        Tooltip,
        InterviewIcons,
        SubscriberList,
        Wysiwyg,
        SelectDropdown,
        InterviewCancel,
        'popup-modal': PopupModal,
        'simple-button': SimpleButton,
        'loading': Loading,
        'simple-textarea': TextArea,
        'question-importer': QuestionImporter,
        'interview-answer': InterviewAnswerComponent,
        'svg-icon': SvgIcon,
        'simple-input': Input,
        'print-content': PrintContent,
    },
    props: {
        application: {
            required: true,
            type: Application,
        },
        interview: {
            type: Interview,
            required: true,
        },
        canManage: Boolean,
    },
    data: function () {
        return {
            subTypes: ['required', 'optional'],
            hasAccessToCalendar: false,
            showReason: false,
            showConfirmation: false,
            showUpdateForm: true,
            bulkApplications: [],
            performingAddAnswerRequest: false,
            printing: false,
            deletingAnswer: null,
            form: new Form([
                {name: 'feedback', value: this.interview.feedback},
                {name: 'type', value: this.interview.type},
                {name: 'company_location_id', value: this.interview.company_location_id},
                {name: 'street', value: this.interview.street},
                {name: 'postal_code', value: this.interview.postal_code},
                {name: 'city', value: this.interview.city},
                {name: 'country_id', value: this.interview.country_id},
                {name: 'meeting_url', value: this.interview.meeting_url},
                {name: 'status_id', value: this.interview.status?.id},
                {name: 'starts_at', value: this.interview.starts_at},
                {name: 'name', value: this.interview.name},
                {name: 'duration', value: this.interview.duration ?? 30},
                {name: 'buffer', value: this.interview.buffer},
                {name: 'slot_confirmed', value: !!this.interview.instant},
                {name: 'instant', value: this.interview.instant},
                {name: 'send_confirmation_mail', value: true},
                {name: 'time_slots', value: this.initTimeslots()},
                {name: 'time_slots_remove', value: []},
                {name: 'sync', value: this.interview.sync},
                {name: 'mode', value: this.interview.mode},
                {name: 'calendar_open', value: this.interview.calendar_open},
                {name: 'calendar_time_range', value: this.interview.calendar_time_range}
            ], null, {
                save_current_only: false,
                rescheduling: false,
                selected_timeslot_id: this.interview.selected_timeslot_id,
                send_mail: true,
            }),
            invitationForm: new Form([
                {name: 'confirmation_subject', value: ''},
                {name: 'confirmation_message', value: ''},
            ]),
            performingImportQuestions: false,
            importingQuestions: false,
            managingSubscriptions: false,
            editingType: false,
            editingTimeslots: false,
            savingInterview: false,
            fetchingTemplates: true,

            templateLocale: this.application.locale,
            selectedTemplate: 0,
            selectedSignature: 0,

            updateForm: {
                attachments: [],
                subject: null,
                message: null,
            },

            cancelingInterview: false,
            cancellationMail: {
                attachments: [],
                subject: '',
                message: ''
            },

            interviewTemplates: null,
            defaultInterviewTemplate: null,
            fetchingInterview: true,
            fetchingDefaultQuestions: false,
            fetchingDefaultTemplate: false,
            subscriptionModel: 'App\\Models\\Subscription',
        };
    },
    computed: {
        ...mapState(useAuthenticationStore, ['currentUserId', 'defaultSignature']),
        ...mapState(useMainStore, ['currentCompany', 'selectedLocale', 'locale']),
        user() {
            return useRepo(User).find(this.currentUserId);
        },
        active_locale() {
            return this.currentCompany.jobsite.active_languages.includes(this.locale) ? this.locale : this.currentCompany.jobsite.active_languages[0];
        },
        submitButtonLabel() {
            if (this.isCanceled) return this.$t('form.submit')

            return (this.form.rescheduling || this.formHasNotifiableProps) && !this.instant ?
                this.$t('general.send') :
                this.$t('general.save_subject', {subject: this.$t('interview.interview')})
        },
        managingQuestions() {
            return this.performingImportQuestions || this.performingAddAnswerRequest || this.fetchingDefaultQuestions;
        },
        confirmMessage() {
            const bulkLength = this.form.options.save_current_only ? 1 : this.bulkApplications.length;
            if (!this.formHasNotifiableProps) return this.$t('general.save_subject', {subject: this.$t('interview.interview')});
            if (!this.form.calendar_open && this.isBulk) {
                return this.$t('mailer.more_candidates_then_timeslots') + this.$tc('mailer.confirmation', bulkLength, {count: bulkLength});
            } else {
                return this.$tc('mailer.confirmation', bulkLength, {count: bulkLength});
            }
        },
        formatFirstTimeslot() {
            return moment.utc(this.form.data.time_slots[0].date).local().format('DD-MM-YYYY HH:mm');
        },
        isBulk() {
            return this.bulkApplications.length > this.form.time_slots.length;
        },
        isCanceled() {
            return this.interview.status?.id === 6 || this.interview.status?.id === 7;
        },
        actionAllowed() {
            return this.canManage && !this.isCanceled;
        },
        canManageTimeSlots() {
            return this.actionAllowed && (!this.interviewIsConfirmed || this.form.rescheduling);
        },
        formTitle() {
            if (this.form.rescheduling) {
                return this.$t('interviews.reschedule.confirmation');
            } else if (!this.form.calendar_open && this.isBulk) {
                return this.$t('jobsite.browser_outdated.title');
            } else if (this.form.slot_confirmed) {
                return this.$t('interview.confirm.confirmation');
            } else {
                return this.$t('interviews.update.confirmation');
            }
        },
        formSubject: {
            get() {
                return this.form.slot_confirmed ? this.updateForm.subject : this.invitationForm.confirmation_subject;
            },
            set(value) {
                if (this.form.slot_confirmed) {
                    this.$set(this.updateForm, "subject", value);
                } else {
                    this.$set(this.invitationForm, "confirmation_subject", value);
                }
            }
        },
        confirmTitle() {
            if (this.form.slot_confirmed) {
                return this.$t('interview.confirm_message');
            } else {
                return this.$t('interview.form.reschedule.edit');
            }
        },
        title() {
            return '<span class="font-normal">' + this.$t('interview.interview') + ' ' + this.$t('general.with') + '</span> ' + this.application.fullName;
        },
        answers() {
            return useRepo(InterviewAnswer)
                .where('interview_id', this.interview.id)
                .get()
                .sort((a, b) => a.original_id - b.original_id);
        },
        questions() {
            return this.answers.map(answer => answer.question.trim().toLowerCase());
        },
        subscriptions() {
            return useRepo(Subscription)
                .with('user')
                .where('subable_type', 'interviews')
                .where('subable_id', !this.isValidInterview(this.interview) ? this.interview.id : null)
                .get();
        },
        requiredSubscriptions() {
            return this.subscriptions.filter(sub => sub.required);
        },
        optionalSubscriptions() {
            return this.subscriptions.filter(sub => !sub.required);
        },
        isOnlineMeeting() {
            return ['online', 'google', 'zoom', 'ms-teams', 'phone'].includes(this.form.type);
        },
        isValidLink() {
            return this.form.meeting_url && (this.form.meeting_url !== 'https://');
        },
        interviewUrl() {
            if (this.isOnlineMeeting) {
                return this.form.meeting_url ?? '';
            }

            return null;
        },
        interviewDate() {
            if (!this.form.starts_at) {
                return;
            }

            return moment(this.form.starts_at)
                .locale(this.locale)
                .format('LLLL');
        },
        timeslotsAlerts() {
            return {
                bulkAlert: this.interview.has_bulk_timeslots
            }
        },
        currentTimeslots() {
            return JSON.parse(JSON.stringify(this.form.time_slots));
        },
        templates() {
            return useRepo(EmailTemplate)
                .with('attachments')
                .where('type', 'interview')
                .orderBy('order')
                .get();
        },
        templateChoices() {
            return [
                {value: 0, label: '--- ' + this.$t('general.select') + ' ---'},
                ...this.templates.map(template => ({
                    value: template.id || template.core_template,
                    label: template.name[this.templateLocale],
                })),
            ];
        },
        signatures() {
            return useRepo(EmailSignature).orderBy('created_at').get();
        },
        signaturePreview() {
            return this.signatures.find(signature => signature.id === this.selectedSignature)?.content;
        },
        signatureChoices() {
            return [
                {value: 0, label: this.$t('email_signatures.no_signature_selected')},
                ...this.signatures.map(signature => ({
                    value: signature.id,
                    label: signature.name,
                })),
            ];
        },
        formHasNotifiableProps() {
            if (!this.form?.isDirty()) return false;
            let state = false;
            const notifiableProps = [
                'time_slots', 'company_location_id', 'type', 'duration', 'meeting_url', 'slot_confirmed'
            ];
            for (let i = 0; i < notifiableProps.length; i++) {
                state = this.form?.isDirty(notifiableProps[i]);
                if (state) break;
            }
            return state;
        },
        interviewIsConfirmed() {
            return !!this.interview.starts_at;
        },
        instant() {
            return !!this.interview.instant;
        },
        locations() {
            return useRepo(Location).all();
        },
        companyLocation() {
            if (!this.form.company_location_id)
                return null;

            return this.locations.find(i => parseInt(i.id) === parseInt(this.form.company_location_id));
        },
        location() {
            if (this.interview.location) return this.interview.location;
            if (!this.companyLocation) return null;
            return `${this.companyLocation.street}, ${this.companyLocation.postal_code} ${this.companyLocation.city}, ${this.companyLocation.country}`;
        },
        applicationList() {
            if (!this.bulkApplications.length) return;
            let str = '';
            this.bulkApplications.forEach((app, index) => {
                if (index) str += ', ';
                str += app.attributes.name;
            })
            return str;
        },
        languages() {
            if (this.currentCompany.jobsite.active_languages.length === 1) {
                return;
            }

            let languages = {};

            this.currentCompany.jobsite.active_languages.forEach((field, index) => {
                languages[field] = {
                    title: field,
                };
            });

            return languages;
        },
        currentLocale() {
            return this.selectedLocale || this.currentCompany.locale;
        },
        confirmationContent: {
            get() {
                return this.form.slot_confirmed ? this.updateForm.message : this.invitationForm.confirmation_message;
            },
            set(val) {
                if (this.form.slot_confirmed) {
                    this.updateForm.message = val;
                } else {
                    this.invitationForm.confirmation_message = val;
                }
            }
        },
        rescheduledByInterviewee() {
            // STATUS_RESCHEDULE_BY_INTERVIEWEE (5)
            return this.interview.status.id === 5;
        },
        cancelledByInterviewee() {
            // STATUS_CANCELED_BY_INTERVIEWEE (7)
            return this.interview.status.id === 7;
        },
        reason() {
            if (this.rescheduledByInterviewee) {
                return this.interview.interviewee_reschedule_reason?.replaceAll("\n", '<br/>');
            } else if (this.cancelledByInterviewee) {
                return this.interview.interviewee_cancel_reason?.replaceAll("\n", '<br/>');
            }
            return '';
        },
        subscriberTranslations() {
            return {
                add_myself: this.$t('interview.subs.add_myself'),
                added_myself: this.$t('interview.subs.added_myself'),
                description: this.$t('interview.subs.description'),
                remove_myself: this.$t('interview.subs.remove_myself'),
                removed_myself: this.$t('interview.subs.removed_myself'),
                subscribe: this.$t('interview.subs.subscribe'),
                subscribed_title: this.$t('interview.subs.subscribed_title'),
                title: this.$t('interview.subs.title'),
                unsubscribe: this.$t('interview.subs.unsubscribe'),
                unsubscribed_title: this.$t('interview.subs.unsubscribed_title'),
            };
        }
    },
    methods: {
        ...mapActions(useCompaniesStore, ['hasFeature']),
        ...mapActions(useInterviewStore, ['setAnswersForTemplateQuestions']),
        isValidInterview(interview) {
            return !interview || !interview.id || isNaN(interview.id);
        },
        subscriptionList(type) {
            if (type === 'required') {
                return this.requiredSubscriptions;
            } else {
                return this.optionalSubscriptions;
            }
        },
        startDate(date) {
            if (!date) return '';
            moment.locale(this.locale);
            return moment(date).format('LLLL');
        },
        endDate(date) {
            if (!this.form.duration) return '';
            moment.locale(this.locale);
            return moment(date).add(this.form.duration, 'minutes').format('LT');
        },
        formatDateTimeWithDuration(date) {
            return `${date} (${this.form.duration} ${this.$t('interview.minutes')})`;
        },
        initTimeslots() {
            if (this.interview.instant && !this.interview.timeslots.length) {
                return [{
                    id: null,
                    date: moment(),
                    available: true,
                    selected_application_id: null
                }]
            }

            return this.interview.timeslots.map(data => ({
                id: data.id,
                date: data.starttime,
                available: data.available,
                selected_application_id: data.selected_application_id
            }));
        },
        async addAnswer() {
            if (this.managingQuestions) return;
            this.performingAddAnswerRequest = true;
            let status = true;

            if (this.isValidInterview(this.interview)) {
                status = await this.saveInterview({
                    force_save: true,
                    close_on_save: false
                }).then((result) => {
                    const interviewId = result.id;
                    this.updateInterview(interviewId);
                    return result;
                });
            }

            if (status) {
                interviews.createAnswer(this.interview.id, {
                    data: {
                        attributes: {question: '', answer: ''},
                        relationships: {
                            interview: {
                                data: {
                                    type: 'interviews',
                                    id: this.interview.id,
                                },
                            },
                        },
                    },
                }).then(result => {
                    return useRepo(InterviewAnswer).save(normalizeJson(result));
                }).then(inserts => {
                    this.performingAddAnswerRequest = false;
                    this.$refs['answer-' + inserts.id][0].focus();
                });
            }
        },
        async importDefaultTemplateQuestions() {
            if (this.managingQuestions) return;
            if (!this.defaultInterviewTemplate) return false;
            this.fetchingDefaultQuestions = true;

            if (this.isValidInterview(this.interview)) {
                return await this.saveInterview({
                    force_save: true,
                    close_on_save: false
                }).then((result) => {
                    const interviewId = result.id;
                    this.updateInterview(interviewId);
                    return this.importTemplateQuestions(
                        interviewId,
                        this.defaultInterviewTemplate
                    );
                });
            }

            this.importTemplateQuestions(this.interview.id, this.defaultInterviewTemplate);
        },
        importTemplateQuestions(interviewId, template) {
            return this.setAnswersForTemplateQuestions({
                interviewId: interviewId,
                template: template
            }).then(() => {
                this.fetchingDefaultQuestions = false;
                this.importingQuestions = false;
            });
        },
        async importQuestions() {
            if (this.managingQuestions) return;
            this.performingImportQuestions = true;
            let status = true;
            if (this.isValidInterview(this.interview)) {
                status = await this.saveInterview({
                    force_save: true,
                    close_on_save: false
                }).then((result) => {
                    this.updateInterview(result.id);
                    return result;
                });
            }
            if (status) this.importingQuestions = true;
        },
        stopImportingQuestions() {
            this.performingImportQuestions = false;
            this.importingQuestions = false;
        },
        manageSubscriptions() {
            this.managingSubscriptions = true;
        },
        stopManagingSubscribers() {
            this.managingSubscriptions = false;
        },
        toggleEditingType() {
            this.editingType = !this.editingType;
        },
        startEditingTimeslots() {
            this.editingTimeslots = true;
        },
        stopEditingTimeslots(mode) {
            this.form.mode = 'manually';
            if (mode === 'close') {
                this.form.reset('time_slots', this.form.original.time_slots);
            }
            this.editingTimeslots = false;
        },
        async saveInterview(options = {}) {
            const forceSave = options.hasOwnProperty('force_save') ? options.force_save : false;
            const closeOnSave = options.hasOwnProperty('close_on_save') ? options.close_on_save : true;
            const modalToClose = options.hasOwnProperty('modal_to_close') ? options.modal_to_close : null;
            let invitationData = this.invitationForm;

            if (!forceSave && !this.form?.isDirty() && !modalToClose) {
                this.$emit('close');
                return;
            }

            if (!this.form.data.duration && this.interview.invitation) {
                return false;
            }

            this.savingInterview = true;
            let relationships = {
                invitation: {
                    data: {
                        confirmation_subject: this.form.slot_confirmed ? this.updateForm.subject : invitationData.confirmation_subject,
                        confirmation_message: (this.form.slot_confirmed ? this.updateForm.message : invitationData.confirmation_message) + (this.signaturePreview ? ('<br>' + this.signaturePreview) : ''),
                        duration: invitationData.duration,
                    },
                },
            };

            let timeSlots = this.form.data.time_slots;

            if (this.form.instant) {
                this.form.starts_at = timeSlots[0].date;
            }

            if (this.form.slot_confirmed && !this.instant && timeSlots.length > 1) {
                let firstSlot = timeSlots.shift();
                this.form.data.time_slots_remove = timeSlots;
                timeSlots = [firstSlot];
            }

            if (timeSlots) {
                relationships = {
                    ...relationships,
                    time_slots: {
                        update: timeSlots,
                        destroy: this.form.data.time_slots_remove
                    }
                }
            }

            if (this.interview.id && isNumber(this.interview.id)) {
                this.form.submit(data => {
                    let xhrData = {
                        data: {
                            attributes: data,
                            options: this.form.options,
                            relationships,
                        },
                    }

                    // add mail to request if notifiable props are changed
                    if (this.formHasNotifiableProps) {
                        if (this.signaturePreview) {
                            this.updateForm.message = this.updateForm.message + (this.signaturePreview ? ('<br>' + this.signaturePreview) : '');
                        }
                        xhrData.data['mail'] = this.updateForm;
                    }
                    return interviews.update(this.interview.id, xhrData).then(result => {
                        this.interview.timeslots.forEach(timeslot => !result.relationships.timeslots.find(x => x.id === timeslot.id) ? useRepo(Timeslot).destroy(timeslot.id) : null)
                        return useRepo(Interview).update(normalizeJson(result));
                    }).then(inserts => {
                        if (modalToClose) {
                            modalToClose();
                        } else {
                            this.$emit('close');
                        }
                        this.savingInterview = false;
                    })
                });
            } else {
                return this.form.submit(data =>
                    interviews.create(this.application.id, {
                        data: {
                            attributes: data,
                            relationships: {
                                application: {
                                    data: {
                                        type: 'applications',
                                        id: this.application.id,
                                    },
                                },
                                user: {
                                    data: {
                                        type: 'users',
                                        id: this.currentUserId,
                                    },
                                },
                                time_slots: relationships.time_slots,
                                invitation: {
                                    data: {
                                        confirmation_subject: invitationData.confirmation_subject,
                                        confirmation_message: invitationData.confirmation_message + (this.signaturePreview ? ('<br>' + this.signaturePreview) : ''),
                                        duration: invitationData.duration,
                                    },
                                },
                                subscriptions: this.subscriptions.map(sub => ({
                                    data: {
                                        type: 'subscriptions',
                                        user_id: sub.user.id,
                                        required: sub.required,
                                    }
                                })),
                            },
                        },
                    }).then(result => {
                        return useRepo(Interview).save(normalizeJson(result));
                    }).then(inserts => {
                        if (closeOnSave) {
                            if (modalToClose) {
                                modalToClose();
                            } else {
                                this.$emit('close');
                            }
                        }

                        this.savingInterview = false;

                        return inserts;
                    })
                );
            }
        },
        updateInterview(interviewId) {
            const interview = useRepo(Interview).find(interviewId);
            this.$emit('update', interview);
        },
        getDefaultTemplate() {
            let templateId = this.application.stage.job.interview_template_id;
            if (!templateId) return false;

            this.fetchingDefaultTemplate = true;
            return interviewTemplateService.show(templateId, ['questions']).then((data) => {
                useRepo(InterviewTemplate).save(normalizeJson(data.data));
                this.defaultInterviewTemplate = useRepo(InterviewTemplate).find(templateId);
                this.fetchingDefaultTemplate = false;
            });
        },
        getApplications() {
            interviews.bulkApplications(this.interview.id)
                .then((data) => {
                    this.bulkApplications = data;
                    // if more than 1 applications, it's bulk
                    this.form.options.save_current_only = this.bulkApplications.length > 1;
                })
        },
        submitHandler() {
            if (this.isCanceled) {
                return this.saveFeedback({
                    force_save: this.interview.instant
                });
            }

            if (this.interview.instant) {
                return this.saveInterview({
                    force_save: this.interview.instant
                });
            }

            if (!this.form.isDirty()) return this.$emit('close');

            this.showConfirmation = true;
        },
        saveFeedback() {
            this.savingInterview = true;

            if (!this.form?.isDirty('feedback')) return this.$emit('close');

            return interviews.feedback(this.interview.id, { feedback: this.form.feedback }).then(async result => {
                return useRepo(Interview).save(normalizeJson(result));
            }).then(() => {
                this.savingInterview = false;
                return this.$emit('close');
            });
        },
        rescheduleHandler() {
            this.form.rescheduling = true;
            window.bus.dispatch('popup-top');
        },
        cancelInterview(signature) {
            this.savingInterview = true;

            // add signature if present
            if (signature) {
                this.cancellationMail.message += signature;
            }

            interviews.cancel(this.interview.id, {
                data: {
                    attributes: {
                        subject: this.cancellationMail.subject,
                        message: this.cancellationMail.message,
                        feedback: this.form.feedback
                    },
                    options: {
                        send_mail: this.form.send_mail
                    }
                },
            }).then(async ({data}) => {
                this.cancelingInterview = false;
                useRepo(TimelineEvent).save(normalizeJson(data.relationships.timeline_events));
                return useRepo(Interview).where('id', data.id).update(normalizeJson(data));
            }).then((inserts) => {
                const interview = inserts[0];
                this.rehydrateForm(interview);
                this.updateInterview(interview.id);
                this.savingInterview = false;
                // The following line is necessary because VueX ORM refuses to update the reactive property this.interview (used to build this.form)
                this.form.status_id = interview.status.id;
            });
        },
        rehydrateForm(interview, original = false) {
            for (const [field] of Object.entries(this.form.data)) {
                if (!this.form?.isDirty(field)) {
                    if (interview[field]) {
                        if (original) {
                            this.form.original[field] = interview[field];
                            this.form.data[field] = this.form.original[field];
                        } else {
                            this.form.data[field] = interview[field];
                        }
                    }
                }
            }
            if (original) {
                this.form.originalOptions['selected_timeslot_id'] = interview.selected_timeslot_id;
                this.form.options['selected_timeslot_id'] = this.form.originalOptions.selected_timeslot_id;
            } else {
                this.form.options['selected_timeslot_id'] = interview.selected_timeslot_id;
            }
        },
        hydrateFromTemplate(data) {
            let templateId = !isNaN(data) ? data : this.selectedTemplate;

            this.templateLocale = this.application.locale;

            let template = this.templates.find(template => template.id === templateId);
            //Always empty attachments
            this.updateForm.attachments = [];

            if (typeof template != 'undefined') {
                //Check for attachments
                if (template.attachments.length > 0) {
                    //Add attachments to form by locale
                    this.updateForm.attachments = template.attachments.filter(obj => obj.locale === this.active_locale);
                }

                if (!this.form.slot_confirmed) {
                    this.updateForm.subject = template.subject[this.templateLocale];
                    this.$refs.content.setContent(template.content[this.templateLocale]);
                    this.invitationForm.confirmation_subject = template.confirmation_subject[this.templateLocale];
                    this.$refs.confirmation_content.setContent(template.confirmation_content[this.templateLocale]);
                } else {
                    this.updateForm.subject = template.confirmation_subject[this.templateLocale];
                    this.$refs.confirmation_content.setContent(template.confirmation_content[this.templateLocale]);
                }
            }
        },
        cancelSendingMail() {
            this.showConfirmation = !this.showConfirmation;
            this.selectedTemplate = 0;
            this.updateForm.subject = '';
            this.updateForm.message = '';
            this.invitationForm.confirmation_subject = '';
            this.invitationForm.confirmation_message = '';
        },
        setAccessToFeature() {
            return new Promise(resolve => {
                this.hasAccessToCalendar = !!this.hasFeature('calendar')
                resolve();
            });
        }
    },
    watch: {
        'form.calendar_open'() {
            if (this.form.calendar_open) {
                this.form.time_slots = [];
            } else {
                this.form.calendar_time_range = null;
            }
        },
    },
    created() {
        if (this.interview.id && Number.isInteger(this.interview.id)) {
            interviews.show(this.interview.id)
                .then(async data => {
                    return useRepo(Interview).save(normalizeJson(data));
                })
                .then(result => {
                    this.rehydrateForm(result, true)
                    if (result.timeslots?.length) {
                        this.form.original.time_slots = result.timeslots.map(slot => ({
                            id: slot.id,
                            date: slot.starttime,
                            available: slot.available,
                            selected_application_id: slot.selected_application_id
                        }));
                        this.form.time_slots = JSON.parse(JSON.stringify(this.form.original.time_slots));
                    }
                    this.fetchingInterview = false
                });
        } else {
            this.fetchingInterview = false;
        }
    },
    mounted() {
        this.templateLocale = this.application.locale;
        this.setAccessToFeature()
            .then(() => {
                if (this.hasAccessToCalendar) {
                    this.form.sync = this.user.cronofy_auto_sync;
                }
            });
        this.getDefaultTemplate();
        useRepo(Subscription).save(normalizeJson({
                attributes: {
                    user_id: this.currentUserId,
                    company_id: this.application.company_id,
                    subable_id: null,
                    subable_type: 'interviews',
                    required: true,
                },
                type: 'subscriptions',
            }));

        signatures.index().then(result => {
            return useRepo(EmailSignature).save(normalizeJson(result.data));
        }).then(() => {
            this.selectedSignature = this.defaultSignature?.id || 0;
        });

        templates.index().then(result => {
            return useRepo(EmailTemplate).save(normalizeJson(result.data));
        }).then(() => {
            this.fetchingTemplates = false;
        });

        if (this.interview.id && isNumber(this.interview.id)) {
            this.getApplications();
        }
    },
    beforeUnmount() {
        useRepo(Subscription)
            .where('subable_type', 'interviews')
            .where((s) => {
                return !s.subable_id;
            })
            .delete();
    },
};
</script>
